import {
  setAffixPool,
  setItemCatalogue,
  setMobCatalogue,
  setNpcCatalogue,
  setQuestCatalogue,
  setSpellCatalogue,
  type ServerMessage,
} from "@vinyl/shared";
import { GameClient } from "./net/client.js";
import { clearToken, loadRememberMe, loadToken, saveToken } from "./net/storage.js";
import { type Screen, Store } from "./state.js";
import { renderEmailScreen, renderOtpScreen } from "./ui/auth.js";
import { renderBoot } from "./ui/boot.js";
import { renderCharacterCreate } from "./ui/character-create.js";
import { type InGameView, createInGameView } from "./ui/connected.js";
import { mount } from "./ui/dom.js";
import "./styles.css";

// Dev: Vite serves the page on :5173, backend is on :3001 (plain ws).
// Prod: page served via https by Caddy, which proxies /ws → :3001 (wss).
const SERVER_URL =
  location.port === "5173"
    ? `ws://${location.hostname}:3001`
    : `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/ws`;

const root = document.getElementById("app");
if (!root) throw new Error("missing #app");

const store = new Store();
const client = new GameClient(SERVER_URL);

let inGameView: InGameView | null = null;
let lastScreenKind: Screen["kind"] | null = null;

function render(): void {
  const state = store.get();
  const kind = state.screen.kind;

  if (kind !== lastScreenKind) {
    if (inGameView) {
      inGameView.destroy();
      inGameView = null;
    }
    lastScreenKind = kind;
  }

  switch (kind) {
    case "boot":
      mount(root!, renderBoot(state.connected));
      return;
    case "auth_email":
      mount(root!, renderEmailScreen(store, client));
      return;
    case "auth_otp":
      mount(root!, renderOtpScreen(store, client));
      return;
    case "char_create":
      mount(root!, renderCharacterCreate(store, client));
      return;
    case "in_game":
      if (!inGameView) {
        inGameView = createInGameView(store, client);
        mount(root!, inGameView.root);
      } else {
        inGameView.update(state);
      }
      return;
  }
}

store.subscribe(render);

client.onConnection((open) => {
  store.set({ connected: open });
  if (open) {
    const existing = loadToken();
    if (existing) {
      store.setScreen({ kind: "boot" });
      client.send({ t: "auth", d: { token: existing } });
    } else if (store.get().screen.kind === "boot") {
      store.setScreen({ kind: "auth_email", submitting: false });
    }
  } else {
    if (store.get().screen.kind === "in_game") {
      store.setScreen({ kind: "boot" });
    }
  }
});

client.onMessage((msg) => handleServer(msg));
client.connect();
render();

function handleServer(msg: ServerMessage): void {
  const screen = store.get().screen;
  switch (msg.t) {
    case "serverHello":
      return;
    case "otpSent": {
      const remember = loadRememberMe();
      const email =
        screen.kind === "auth_email" || screen.kind === "auth_otp" ? msg.d.email : msg.d.email;
      store.setScreen({
        kind: "auth_otp",
        email,
        submitting: false,
        devCode: msg.d.devCode,
        rememberMe: remember,
      });
      return;
    }
    case "accessPending": {
      // M12 — email not yet whitelisted. Bounce the user back to the email
      // screen with an explanation; no OTP was sent.
      store.setScreen({
        kind: "auth_email",
        submitting: false,
        error:
          "Vous n'avez pas encore de compte — votre demande doit être validée manuellement. Retentez plus tard.",
      });
      return;
    }
    case "accessRateLimited": {
      // M12 — too many requestOtp from this IP within the rolling window.
      // Generic "try again later" without leaking the exact bucket size.
      const mins = Math.max(1, Math.ceil(msg.d.retryAfterMs / 60_000));
      store.setScreen({
        kind: "auth_email",
        submitting: false,
        error: `Trop de tentatives depuis votre réseau. Réessayez dans environ ${mins} min.`,
      });
      return;
    }
    case "authOk": {
      saveToken(msg.d.token, screen.kind === "auth_otp" ? screen.rememberMe : loadRememberMe());
      if (msg.d.hasCharacter && msg.d.character) {
        store.setScreen({ kind: "in_game", character: msg.d.character, online: [] });
      } else {
        store.setScreen({ kind: "char_create", submitting: false });
      }
      return;
    }
    case "authError": {
      const reason = humanizeAuthError(msg.d.reason);
      if (screen.kind === "auth_otp") {
        store.setScreen({ ...screen, submitting: false, error: reason });
      } else if (screen.kind === "boot") {
        clearToken();
        store.setScreen({ kind: "auth_email", submitting: false, error: reason });
      } else {
        store.setScreen({ kind: "auth_email", submitting: false, error: reason });
      }
      return;
    }
    case "characterCreated": {
      store.setScreen({ kind: "in_game", character: msg.d.character, online: [] });
      return;
    }
    case "characterError": {
      if (screen.kind === "char_create") {
        store.setScreen({
          kind: "char_create",
          submitting: false,
          error: humanizeCharError(msg.d.reason),
        });
      }
      return;
    }
    case "presence": {
      if (screen.kind === "in_game") {
        store.setScreen({ ...screen, online: msg.d.players });
      }
      return;
    }
    case "rateLimited": {
      const seconds = Math.ceil(msg.d.retryAfterMs / 1000);
      const error = `Too fast. Try again in ${seconds}s.`;
      if (screen.kind === "auth_email") {
        store.setScreen({ ...screen, submitting: false, error });
      } else if (screen.kind === "in_game") {
        // M6.2 — in-game flood / per-type cap. Surface via the in-game toast
        // (handler is wired through the connected view to share styling
        // with equip/vendor/party errors).
        inGameView?.onRateLimited(msg.d.retryAfterMs);
      }
      return;
    }
    case "heartbeat":
      return;
    case "zoneEnter":
      inGameView?.onZoneEnter(
        {
          zoneId: msg.d.zoneId,
          width: msg.d.width,
          height: msg.d.height,
          isSafe: msg.d.isSafe,
          spawnPoints: msg.d.spawnPoints,
          portals: msg.d.portals,
        },
        msg.d.selfCharacterId,
        { x: msg.d.x, y: msg.d.y },
        msg.d.facing,
      );
      return;
    case "chunkData":
      inGameView?.onChunkData(msg.d);
      return;
    case "worldSnapshot":
      inGameView?.onSnapshot({
        players: msg.d.players,
        mobs: msg.d.mobs,
        projectiles: msg.d.projectiles,
        serverTime: msg.d.serverTime,
      });
      return;
    case "mobDebugSnapshot":
      // Sidecar — only arrives when F3 is toggled ON. Server sends one
      // per tick per debug-subscribed player; merged into entity views
      // for the overlay text.
      inGameView?.onMobDebugSnapshot(msg.d);
      return;
    case "chatPosted":
      inGameView?.onChatPosted(msg.d);
      return;
    case "chatError":
      console.warn("[chat] rejected:", msg.d.reason);
      return;
    case "moveRejected":
      inGameView?.onMoveRejected(msg.d.x, msg.d.y);
      return;
    case "combatHit":
      inGameView?.onCombatHit(msg.d);
      return;
    case "entityDied":
      inGameView?.onEntityDied(msg.d);
      return;
    case "entityRespawned":
      inGameView?.onEntityRespawned(msg.d);
      return;
    case "selfStats":
      inGameView?.onSelfStats(msg.d);
      return;
    case "xpGained":
      inGameView?.onXpGained(msg.d);
      return;
    case "goldGained":
      inGameView?.onGoldGained(msg.d);
      return;
    case "levelUp":
      inGameView?.onLevelUp(msg.d);
      return;
    case "attackError":
      console.warn("[attack] rejected:", msg.d.reason);
      return;
    case "allocateError":
      console.warn("[allocate] rejected:", msg.d.reason);
      return;
    case "inventoryUpdate":
      inGameView?.onInventoryUpdate(msg.d);
      return;
    case "equipError":
      inGameView?.onEquipError(msg.d.reason);
      return;
    case "hotbarUpdate":
      inGameView?.onHotbarUpdate(msg.d);
      return;
    case "hotbarError":
      console.warn("[hotbar] rejected:", msg.d.reason);
      return;
    case "useItemError":
      // "no_effect" = already full → not worth surfacing; others are dev info.
      if (msg.d.reason !== "no_effect") console.warn("[useItem] rejected:", msg.d.reason);
      return;
    case "donUsed":
      inGameView?.onDonUsed(msg.d);
      return;
    case "castStateUpdate":
      inGameView?.onCastStateUpdate(msg.d);
      return;
    case "castError":
      inGameView?.onCastError(msg.d.reason);
      return;
    case "combatHeal":
      inGameView?.onCombatHeal(msg.d);
      return;
    case "buffUpdate":
      inGameView?.onBuffUpdate(msg.d);
      return;
    case "areaEffectSpawned":
      inGameView?.onAreaEffectSpawned(msg.d);
      return;
    case "areaEffectExpired":
      inGameView?.onAreaEffectExpired(msg.d);
      return;
    case "characterDeleted":
      store.setScreen({ kind: "char_create", submitting: false });
      return;
    case "groundBagsUpdate":
      inGameView?.onGroundBagsUpdate(msg.d);
      return;
    case "lootContents":
      inGameView?.onLootContents(msg.d);
      return;
    case "lootError":
      console.warn("[loot] rejected:", msg.d.reason);
      return;
    case "projectileSpawned":
      inGameView?.onProjectileSpawned(msg.d);
      return;
    case "projectileExpired":
      inGameView?.onProjectileExpired(msg.d);
      return;
    case "npcsInZone":
      inGameView?.onNpcsInZone(msg.d);
      return;
    case "vendorPanel":
      inGameView?.onVendorPanel(msg.d);
      return;
    case "vendorResult":
      inGameView?.onVendorResult(msg.d);
      return;
    case "vendorError":
      inGameView?.onVendorError(msg.d.code);
      return;
    case "npcSpeech":
      inGameView?.onNpcSpeech(msg.d);
      return;
    case "npcDialog":
      inGameView?.onNpcDialog(msg.d);
      return;
    case "questLogUpdate":
      inGameView?.onQuestLogUpdate(msg.d);
      return;
    case "questError":
      inGameView?.onQuestError(msg.d.code);
      return;
    case "partyUpdate":
      inGameView?.onPartyUpdate(msg.d);
      return;
    case "partyInviteIncoming":
      inGameView?.onPartyInviteIncoming(msg.d);
      return;
    case "partyError":
      inGameView?.onPartyError(msg.d.code);
      return;
    case "catalogueUpdate": {
      // M7.1 / M7.2 — admin published new content (or initial post-zoneEnter
      // snapshot). Swap the runtime registry so every subsequent tooltip /
      // mob render sees the new templates.
      if (msg.d.catalogue === "items") {
        setItemCatalogue(msg.d.items);
        inGameView?.refreshAfterCatalogueUpdate?.();
      } else if (msg.d.catalogue === "mobs") {
        setMobCatalogue(msg.d.mobs);
        // No explicit refresh: next worldSnapshot picks up new templates.
      } else if (msg.d.catalogue === "affixes") {
        setAffixPool(msg.d.affixes);
      } else if (msg.d.catalogue === "quests") {
        setQuestCatalogue(msg.d.quests);
        inGameView?.refreshAfterCatalogueUpdate?.();
      } else if (msg.d.catalogue === "npcs") {
        setNpcCatalogue(msg.d.npcs);
      } else if (msg.d.catalogue === "spells") {
        setSpellCatalogue(msg.d.spells);
      }
      return;
    }
    case "adminLoginOk":
    case "adminLoginError":
    case "adminPublishResult":
      // The game client ignores admin messages — they're addressed to the
      // /admin app. Server sends `adminLoginOk` only to the session that
      // logged in, so this case path stays unreachable in practice.
      return;
  }
}

function humanizeAuthError(reason: string): string {
  switch (reason) {
    case "otp_wrong":
      return "Incorrect code.";
    case "otp_expired":
      return "Code expired. Request a new one.";
    case "otp_too_many":
      return "Too many attempts. Request a new code.";
    case "otp_no_code":
      return "No active code for this email. Request one first.";
    case "invalid_token":
    case "account_missing":
      return "Your session expired. Please log in again.";
    case "token_revoked":
      return "Your session was ended by an admin. Please log in again.";
    case "email_send_failed":
      return "Email delivery failed. Try again in a moment.";
    case "invalid_message":
    case "malformed_json":
      return "Communication error. Refresh the page.";
    default:
      return "Something went wrong. Please try again.";
  }
}

function humanizeCharError(reason: string): string {
  switch (reason) {
    case "name_taken":
      return "That name is already taken.";
    case "name_reserved":
      return "That name is reserved. Please choose another.";
    case "invalid_name":
      return "Name must be 3–16 letters or numbers.";
    case "not_authenticated":
      return "Session expired. Please log in again.";
    case "already_has_character":
      return "You already have a character.";
    default:
      return "Could not create character.";
  }
}

setInterval(() => {
  if (client.isOpen) client.send({ t: "heartbeat" });
}, 20_000);
